import {
  getNonEmptyString,
  isUnknownRecord,
  WIZ_WEBHOOK_TRIGGER_SOURCES,
  type WizWebhookTriggerSource,
} from './wiz-webhook.schema';

export { isUnknownRecord };

export type WizIntegrationProviderSettings = Record<string, unknown> & {
  wizIssuesRuleId?: unknown;
  wizDetectionsRuleId?: unknown;
  wizThreatsRuleId?: unknown;
  ruleIds?: unknown;
};

export type WizWebhookResolvedTarget = {
  tenantId: string;
};

export type WizWebhookCachedTarget = {
  tenantId: string;
};

export type WizWebhookTargetIndex = Record<string, WizWebhookCachedTarget[]>;

export type WizIntegrationConfigTargetRecord = {
  tenantId: string;
  providerSettings: unknown;
};

export const buildWizWebhookTargetLookupKey = ({
  source,
  ruleId,
}: {
  source: WizWebhookTriggerSource;
  ruleId: string;
}): string => `${source}:${ruleId}`;

export const getExpectedWizRuleIdsForTriggerSource = ({
  providerSettings,
  source,
}: {
  providerSettings: WizIntegrationProviderSettings;
  source: WizWebhookTriggerSource;
}): string[] => {
  const nestedRuleIds = isUnknownRecord(providerSettings.ruleIds)
    ? providerSettings.ruleIds
    : {};

  const directRuleIdBySource: Record<WizWebhookTriggerSource, unknown> = {
    ISSUE: providerSettings.wizIssuesRuleId,
    DETECTION: providerSettings.wizDetectionsRuleId,
    THREAT: providerSettings.wizThreatsRuleId,
  };

  const nestedRuleIdBySource: Record<WizWebhookTriggerSource, unknown> = {
    ISSUE: nestedRuleIds.issues,
    DETECTION: nestedRuleIds.detections,
    THREAT: nestedRuleIds.threats,
  };

  return [directRuleIdBySource[source], nestedRuleIdBySource[source]].flatMap(
    (value) => {
      const stringValue = getNonEmptyString({ value });
      return stringValue ? [stringValue] : [];
    },
  );
};

export const buildWizWebhookTargetIndexFromConfigs = ({
  configs,
}: {
  configs: WizIntegrationConfigTargetRecord[];
}): WizWebhookTargetIndex => {
  const targetIndex: WizWebhookTargetIndex = {};

  for (const config of configs) {
    if (!isUnknownRecord(config.providerSettings)) {
      continue;
    }

    const providerSettings =
      config.providerSettings as WizIntegrationProviderSettings;

    for (const source of WIZ_WEBHOOK_TRIGGER_SOURCES) {
      const ruleIds = new Set(
        getExpectedWizRuleIdsForTriggerSource({ providerSettings, source }),
      );
      for (const cachedRuleId of ruleIds) {
        const lookupKey = buildWizWebhookTargetLookupKey({
          source,
          ruleId: cachedRuleId,
        });
        targetIndex[lookupKey] ??= [];
        targetIndex[lookupKey].push({
          tenantId: config.tenantId,
        });
      }
    }
  }

  return targetIndex;
};
